import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MetaTitle} from '../../../../decorator/meta-title';
import {BillingRequestService} from '../../../../services/accounting/billing-request.service';
import {BillingRequest} from '../../../../models/accounting/billing-request';
import { DatePipe, NgClass } from '@angular/common';
import {Paginator} from '../../../../tools/pagination/paginator';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {DateRange} from '../../../../types/date-range';
import {TranslatePipe} from '@ngx-translate/core';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {AuthService} from '../../../../services/auth/auth.service';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {InvoiceService} from '../../../../services/accounting/invoice.service';
import {FilterStorageService} from '../../../../services/filter-storage/filter-storage.service';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {InvoiceFileComponent} from '../invoice-file/invoice-file.component';

@Component({
  selector: 'app-payment-list',
  imports: [
    DatePipe,
    DateRangeSelectorComponent,
    DateRangeSelectorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    PaginatorComponent,
    PaginatorComponent,
    NgClass,
    TranslatePipe,
    MoneyPipe
],
  providers: [DatePipe],
  templateUrl: './payment-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './payment-list.component.scss'
})
@MetaTitle('Payments')
export class PaymentListComponent implements OnInit {

  filters: FormGroup = new FormGroup({
    startingAt: new FormControl(),
    endingAt: new FormControl(),
    status: new FormControl([]),
    deferred: new FormControl('')
  });

  billingRequests: BillingRequest[] = [];
  invoiceStatusList: string[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('startingAt', 'desc');
  loaded: boolean = false;
  dateRangeInvalid: boolean = false;
  savedDate: any = null;

  @ViewChild(DateRangeSelectorComponent) dateRangeSelector!: DateRangeSelectorComponent;

  constructor(
    protected readonly invoiceService: InvoiceService,
    private readonly billingRequestService: BillingRequestService,
    private readonly auth: AuthService,
    private readonly datePipe: DatePipe,
    private readonly filterStorage: FilterStorageService,
    private readonly dialog: DialogService,
  ) {
  }

  ngOnInit(): void {
    const savedFilters = this.filterStorage.load('payments');
    if (savedFilters) {
      this.filters.patchValue({
        status: savedFilters.status ?? [],
        deferred: savedFilters.deferred ?? '',
      });
      this.savedDate = savedFilters.date ?? null;
    }

    this.auth.getAsyncConfig().subscribe((config) => {
      if (config) {
        this.invoiceStatusList = config?.data?.invoiceStatusList ?? [];

        this.fetchAll();
      }
    });
  }

  fetchAll(): void {
    this.saveFilters();

    this.loaded = false

    const data: any = this.filters.value;
    const parameters: any = {
      groups: 'file'
    };

    if (data.startingAt) {
      parameters.startingAt = this.datePipe.transform(data.startingAt, 'yyyy-MM-dd');
    }
    if (data.endingAt) {
      parameters.endingAt = this.datePipe.transform(data.endingAt, 'yyyy-MM-dd');
    }
    if (data.status && data.status.length) {
      parameters.status = data.status;
    }
    if (data.deferred !== '') {
      parameters.deferred = data.deferred;
    }

    this.billingRequestService.findAll(parameters, this.paginator, this.tableSorter).subscribe((billingRequests: BillingRequest[]) => {
      this.billingRequests = billingRequests;

      this.loaded = true;
    });
  }

  openInvoiceFile(billingRequest: BillingRequest): void {
    this.dialog.openDialog(InvoiceFileComponent, {
      class: 'modal-xl',
      backdrop: false,
      data: {
        billingRequest
      }
    }).subscribe((updated: boolean) => {
      if (updated) {
        this.fetchAll();
      }
    });
  }

  updateRange(dateRange: DateRange): void {
    this.filters.get('startingAt')?.patchValue(dateRange.start);
    this.filters.get('endingAt')?.patchValue(dateRange.end);
  }

  private saveFilters(): void {
    this.filterStorage.save('payments', {
      status: this.filters.value.status,
      deferred: this.filters.value.deferred,
      date: this.dateRangeSelector ? {
        selector: this.dateRangeSelector.dateSelector,
        from: this.dateRangeSelector.dateFromSelector,
        to: this.dateRangeSelector.dateToSelector,
      } : this.savedDate,
    });
  }

}
